import { Spinner } from 'components/core';
import { findModelForEditing, submitServerChange } from 'components/dataCreationForms/common';
import { projectEditOnViewToEditModel } from 'shared/domain/project/mapping/toModel';
import { projectModelToProjectEditOnView } from 'shared/domain/project/mapping/toView';
import { projectClient } from 'shared/domain/project/projectClient';
import { ProjectEditOnView } from 'shared/domain/project/types/view';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { getProjectsStoreState, useProjects } from 'store/projects';
import { EditProjectContext } from '../types';
import {
  handleProjectEditFormValidation,
  validateProjectEdit,
} from '../validation';
import { useOrganizations } from 'store/organizations';
import { useAppNavigation } from 'contexts/navigation';

const formName = 'project';
const SUBMIT_EVENT_TYPE = 'submit-project';
const WithEditProjectContext = createContext<
  EditProjectContext | undefined
>(undefined);

function WithEditProject({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const navigate = useNavigate();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const { organizationsMap } = useOrganizations();
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const { items: projects, loading: loadingProjects } = useProjects();
  const currentProjectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const { goToProjectDetails } = useAppNavigation();
  const [existingValues, setExistingValues] = useState<
    ProjectEditOnView | undefined
  >(undefined);

  useEffect(() => {
    const existingProjectModel = findModelForEditing(
      projects,
      currentProjectId
    );
    let projectEditOnView;
    if (existingProjectModel) {
      projectEditOnView = projectModelToProjectEditOnView(
        existingProjectModel,
        organizationsMap.get(existingProjectModel.organizationId)?.label ||
          ''
      );
      setExistingValues(projectEditOnView);
    }
  }, [projects, currentProjectId, organizationsMap]);

  const [open, setOpen] = useState(false);

  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);

  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );

  const confirmCancel = useCancelConfirmation(formName);

  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: ProjectEditOnView) => {
      const { saved } = await submitServerChange({
        change: () => projectClient.edit(projectEditOnViewToEditModel(values)),
        setOpen,
        setIsPosting,
        action: 'updating a project',
      });
      if (!saved) {
        return;
      }

      // The details view shows the project from the store.
      await getProjectsStoreState().actions.reload();
      goToProjectDetails();
    },
    [goToProjectDetails]
  );

  const ctx: EditProjectContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      existingValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateProjectEdit,
      handleFormValidation: handleProjectEditFormValidation,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    existingValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
  ]);
  if (loadingProjects) {
    return (
      <Spinner
        reason={`ProjectWizard: loadingProjects. ExistingValues: ${existingValues}`}
      />
    );
  }
  return (
    <WithEditProjectContext.Provider value={ctx}>
      {children}
    </WithEditProjectContext.Provider>
  );
}

function useEditProject(): EditProjectContext {
  const context = useContext(WithEditProjectContext);
  if (context === undefined) {
    throw new Error(
      'useEditProject must be used within an EditProjectContext'
    );
  }
  return context;
}

const withEditProject =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithEditProject>
      <Component {...props} />
    </WithEditProject>
  );

export { WithEditProject, useEditProject, withEditProject };
